import {
  ApolloQueryResult,
  OperationVariables,
  QueryOptions,
} from '@apollo/client';
import React, { useEffect, useRef, useState } from 'react';
import { MimicUserModal, querySdn } from '@spins-gcp/shared-components-react';
import { buildMimicPath } from '../../utils/buildMimicPath';
import { useInfiniteScroll } from '../../hooks/useInfiniteScroll';
import { useNavigate } from 'react-router-dom';
import { useMimic } from '../../hooks/useMimic';
import { useCompanies } from '../../hooks/useCompanies';

const DEBOUNCE_DELAY = 300;
const MIN_SEARCH_LENGTH = 2;

export const MimicModal = () => {
  const { setMimicUser, showMimicModal, setShowMimicModal } = useMimic();
  const {
    companies,
    hasMore,
    scrollLoading,
    checkMimicEligibility,
    fetchCompanies,
    loadMoreCompanies,
  } = useCompanies();
  const [companySearch, setCompanySearch] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);
  const navigator = useNavigate();

  useEffect(() => {
    void checkMimicEligibility();
  }, [checkMimicEligibility]);

  useEffect(() => {
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    debounceTimerRef.current = setTimeout(() => {
      setDebouncedSearchTerm(companySearch);
    }, DEBOUNCE_DELAY);

    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, [companySearch]);

  useEffect(() => {
    if (!showMimicModal) {
      return;
    }
    if (debouncedSearchTerm.length >= MIN_SEARCH_LENGTH) {
      void fetchCompanies(debouncedSearchTerm, 10);
    } else if (debouncedSearchTerm.length === 0) {
      void fetchCompanies('', 200);
    }
  }, [debouncedSearchTerm, fetchCompanies, showMimicModal]);

  const { lastElementRef } = useInfiniteScroll({
    callback: () => loadMoreCompanies(debouncedSearchTerm),
    hasMore,
    isLoading: scrollLoading,
  });

  return (
    <MimicUserModal
      appName={'Portal'}
      companies={companies}
      handleClick={
        /*istanbul ignore next*/ (user) => {
          setShowMimicModal(false);
          setMimicUser?.({
            email: user.email,
            companyId: user.companyId,
            name: user.name,
            company: user.company,
          });
          const currentPath = window.location.pathname;
          navigator(buildMimicPath(currentPath, user));
        }
      }
      showMimicModal={showMimicModal}
      onHide={
        /*istanbul ignore next*/ () => {
          setShowMimicModal(false);
          setCompanySearch('');
        }
      }
      querySdn={
        querySdn as <T, TVariables = OperationVariables>(
          query: QueryOptions<TVariables, T>
        ) => Promise<ApolloQueryResult<T>>
      }
      setCompanySearch={setCompanySearch}
      lastElementRef={
        lastElementRef as unknown as React.RefObject<HTMLButtonElement>
      }
    />
  );
};
